import PocketBase, { RecordModel } from 'pocketbase';

// Get API URL from environment variable
const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8090';
console.log('API URL:', apiUrl);

const pb = new PocketBase(apiUrl);

// Enable auto refresh of auth tokens
pb.autoCancellation(false);

// Log auth state changes for debugging
pb.authStore.onChange(() => {
    console.log('Auth state changed:', {
        isValid: pb.authStore.isValid,
        token: pb.authStore.token,
        model: pb.authStore.model
    });
});

export interface SelectOption {
    value: string;
    label: string;
}

export interface Auditor extends RecordModel {
    name: string;
    vorname: string;
    nachname: string;
    email: string;
    is_auditor: boolean;
    role_level: number;
    avatar?: string;
}

export interface Direktion extends RecordModel {
    name: string;
}

export interface Dienststelle extends RecordModel {
    name: string;
    direktion: string;
    expand?: {
        direktion?: Direktion;
    };
}

export interface Kontrollpunkt extends RecordModel {
    sicherheitssystem: string;
}

export interface Bereich extends RecordModel {
    name: string;
    dienststelle: string;
    adresse?: string;
    bemerkungen?: string;
}

export interface IstZustandMassnahme extends RecordModel {
    audit: string;
    reihenfolge: number;
    nummer: number;
    istzustand: string;
    massnahme: string;
    bereich: string;
    erledigt: boolean;
    bemerkung?: string;
    anhaenge?: string[];
}

export interface Audit extends RecordModel {
    auditnummer: string;
    auditdatum: string;
    auditor: string[];
    direktion?: string;
    dienststelle: string;
    bereich?: string;
    adresse: string;
    kontrollpunkt: string[];
    begleitpersonen?: string;
    expand?: {
        auditor?: Auditor[];
        direktion?: Direktion;
        dienststelle?: Dienststelle;
        bereich?: Bereich;
        kontrollpunkt?: Kontrollpunkt[];
    };
}

async function retryOperation<T>(
    operation: () => Promise<T>,
    retries = 3,
    delay = 1000
): Promise<T> {
    try {
        return await operation();
    } catch (error) {
        if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, delay));
            return retryOperation(operation, retries - 1, delay * 2);
        }
        throw error;
    }
}

export async function getNextAuditNumber(): Promise<string> {
    return retryOperation(async () => {
        const result = await pb.collection('audits').getList<Audit>(1, 1, {
            sort: '-auditnummer',
            $autoCancel: false
        });

        if (result.items.length === 0) {
            return '100';
        }

        const lastNumber = parseInt(result.items[0].auditnummer);
        return (lastNumber + 1).toString();
    });
}

export async function generateAuditNumber(): Promise<string> {
    return getNextAuditNumber();
}

export async function fetchAuditoren(): Promise<SelectOption[]> {
    return retryOperation(async () => {
        try {
            // Get all users
            const records = await pb.collection('users').getFullList<Auditor>({
                sort: 'name',
                $autoCancel: false
            });

            // Filter users who can be auditors
            const auditors = records.filter(record => record.is_auditor === true);

            // Map to select options with proper name formatting
            return auditors.map(record => ({
                value: record.id,
                label: record.name || record.email || 'Unbekannt'
            }));
        } catch (error) {
            console.error('Error fetching auditors:', error);
            return [];
        }
    });
}

export async function fetchDirektionen(): Promise<SelectOption[]> {
    return retryOperation(async () => {
        const records = await pb.collection('direktionen_gemeinden').getFullList<Direktion>({
            sort: 'name',
            $autoCancel: false
        });
        return records.map(record => ({
            value: record.id,
            label: record.name
        }));
    });
}

export async function fetchDienststellen(direktionId?: string): Promise<SelectOption[]> {
    return retryOperation(async () => {
        const filter = direktionId ? `direktion = "${direktionId}"` : '';
        const records = await pb.collection('dienststellen').getFullList<Dienststelle>({
            filter,
            sort: 'name',
            expand: 'direktion',
            $autoCancel: false
        });
        return records.map(record => ({
            value: record.id,
            label: record.name
        }));
    });
}

export async function fetchBereiche(dienststelleId: string): Promise<SelectOption[]> {
    return retryOperation(async () => {
        const records = await pb.collection('bereich_abteilung').getFullList<Bereich>({
            filter: `dienststelle = "${dienststelleId}"`,
            sort: 'name',
            $autoCancel: false
        });
        return records.map(record => ({
            value: record.id,
            label: record.name
        }));
    });
}

export async function getBereichAdresse(bereichId: string): Promise<string> {
    return retryOperation(async () => {
        const record = await pb.collection('bereich_abteilung').getOne<Bereich>(bereichId, {
            $autoCancel: false
        });
        return record.adresse || '';
    });
}

export async function fetchKontrollpunkte(): Promise<SelectOption[]> {
    return retryOperation(async () => {
        try {
            const records = await pb.collection('kontrollpunkte').getFullList<Kontrollpunkt>({
                sort: 'sicherheitssystem',
                $autoCancel: false
            });
            return records.map(record => ({
                value: record.id,
                label: record.sicherheitssystem
            }));
        } catch (error) {
            console.error('Error fetching kontrollpunkte:', error);
            return [];
        }
    });
}

export async function createAudit(data: Omit<Audit, keyof RecordModel | 'auditnummer'>): Promise<Audit> {
    if (!pb.authStore.isValid) {
        throw new Error('Not authenticated');
    }
    return retryOperation(async () => {
        const auditNumber = await generateAuditNumber();
        const payload: any = {
            ...data,
            auditnummer: auditNumber
        };
        
        return await pb.collection('audits').create<Audit>(payload);
    });
}

export async function updateAudit(id: string, data: Partial<Omit<Audit, keyof RecordModel>>): Promise<Audit> {
    if (!pb.authStore.isValid) {
        throw new Error('Not authenticated');
    }

    // Normalize relation fields to ensure only string IDs are sent
    function normalizeField(value: any): any {
        if (Array.isArray(value)) {
            return value.map(v => (typeof v === 'string' ? v : v?.id || v));
        }
        if (typeof value === 'object' && value !== null && value.id) {
            return value.id;
        }
        return value;
    }

    const payload: any = { ...data };

    payload.auditor = normalizeField(payload.auditor);
    payload.dienststelle = normalizeField(payload.dienststelle);
    payload.kontrollpunkt = normalizeField(payload.kontrollpunkt);

    // Fetch the existing audit to include all required fields
    const existing = await pb.collection('audits').getOne<Audit>(id);

    // Merge missing required fields from existing record
    const requiredFields = ['auditnummer', 'auditdatum', 'auditor', 'dienststelle', 'kontrollpunkt'];
    for (const field of requiredFields) {
        if (payload[field] === undefined || payload[field] === null || payload[field]?.length === 0) {
            payload[field] = normalizeField(existing[field]);
        }
    }

    return retryOperation(async () => {
        return await pb.collection('audits').update<Audit>(id, payload);
    });
}

export async function createMassnahme(data: FormData): Promise<IstZustandMassnahme> {
    if (!pb.authStore.isValid) {
        throw new Error('Not authenticated');
    }
    return retryOperation(async () => {
        return await pb.collection('ist_zustand_massnahmen').create<IstZustandMassnahme>(data);
    });
}

export async function updateMassnahme(id: string, data: FormData): Promise<IstZustandMassnahme> {
    if (!pb.authStore.isValid) {
        throw new Error('Not authenticated');
    }
    return retryOperation(async () => {
        return await pb.collection('ist_zustand_massnahmen').update<IstZustandMassnahme>(id, data);
    });
}

export async function deleteMassnahme(id: string): Promise<boolean> {
    if (!pb.authStore.isValid) {
        throw new Error('Not authenticated');
    }
    return retryOperation(async () => {
        await pb.collection('ist_zustand_massnahmen').delete(id);
        return true;
    });
}

interface FormDataInput {
    audit?: string;
    reihenfolge?: number;
    nummer?: number;
    istzustand?: string;
    massnahme?: string;
    bereich?: string;
    erledigt?: boolean;
    bemerkung?: string;
    anhaenge?: (string | File)[];
}

export function prepareFormData(data: FormDataInput): FormData {
    const formData = new FormData();
    
    // Add required fields
    if (data.audit) formData.append('audit', data.audit);
    if (typeof data.reihenfolge === 'number') formData.append('reihenfolge', data.reihenfolge.toString());
    if (typeof data.nummer === 'number') formData.append('nummer', data.nummer.toString());
    if (data.istzustand) formData.append('istzustand', data.istzustand);
    if (data.massnahme) formData.append('massnahme', data.massnahme);
    if (data.bereich) formData.append('bereich', data.bereich);
    
    // Add optional fields
    if (typeof data.erledigt === 'boolean') formData.append('erledigt', String(data.erledigt));
    if (data.bemerkung) formData.append('bemerkung', data.bemerkung);
    
    // Handle files - both existing and new
    if (data.anhaenge) {
        // Handle existing file references (strings)
        const existingFiles = data.anhaenge.filter((file): file is string => typeof file === 'string');
        if (existingFiles.length > 0) {
            formData.append('anhaenge', JSON.stringify(existingFiles));
        }

        // Handle new files
        const newFiles = data.anhaenge.filter((file): file is File => file instanceof File);
        newFiles.forEach(file => {
            formData.append('anhaenge', file);
        });
    }
    
    return formData;
}

export { pb };
